import type { Scene } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';
import { $dfs, $dfsIterator, $getNearestNodeOfType } from '@lexical/utils';
import type {
    BaseSelection,
    CreateEditorArgs,
    EditorUpdateOptions,
    ElementNode,
    LexicalEditor,
    LexicalNode,
    SerializedElementNode,
    SerializedLexicalNode,
} from 'lexical';
import {
    $getRoot,
    $getSelection,
    $isElementNode,
    $isLineBreakNode,
    $nodesOfType,
    createEditor,
} from 'lexical';

import { $isSceneNumberNode, SceneNumberNode } from './nodes/SceneNumberNode';

import type {
    EditedTranscriptWord,
    EditedTranscriptWordRequest,
    Pronunciation,
    TranscriptWord,
} from 'js/src/client';
import {
    MAX_PRONUNCIATION_WORDS,
    STRIPPED_PUNCTUATIONS_REGEX,
} from 'js/src/data/pronunciations/pronunciations-types';
import type { DFSIterator } from 'js/src/data/transcript/mirrored-dfs-iterator';
import {
    $mirroredDfsIterator,
    emptyDFSIterator,
} from 'js/src/data/transcript/mirrored-dfs-iterator';
import { BaseOverlayElementNode } from 'js/src/data/transcript/nodes/BaseOverlayElementNode';
import { DragHandleElementNode } from 'js/src/data/transcript/nodes/DragHandleElementNode';
import {
    $isOverlayElementNode,
    OverlayElementNode,
} from 'js/src/data/transcript/nodes/OverlayElementNode';
import {
    $isSilenceNode,
    $isTranscriptWordNodeAndNotSilence,
    SilenceNode,
} from 'js/src/data/transcript/nodes/SilenceNode';
import { TextCorrectElementNode } from 'js/src/data/transcript/nodes/TextCorrectElementNode';
import {
    $createTranscriptWordNode,
    $isEmptyTranscriptWordNodeCreatedByTextCorrection,
    $isTranscriptWordNode,
    EMPTY_WORD_CREATED_BY_TEXT_CORRECTION,
    TranscriptWordNode,
} from 'js/src/data/transcript/nodes/TranscriptWordNode';
import { $isTrimElementNode, TrimElementNode } from 'js/src/data/transcript/nodes/TrimElementNode';
import { TrimSymbolNode } from 'js/src/data/transcript/nodes/TrimSymbolNode';
import type { SilenceNodeData } from 'js/src/data/transcript/transcript-types';
import { wordGapMidpoint } from 'js/src/data/transcript/word-timing';
import type { DeepReadonly } from 'js/src/libs/consts';
import { isNotUndefined, TRANSCRIPT_CONFIG } from 'js/src/libs/consts';
import { type Context, ErrorReporter } from 'js/src/libs/error-reporter';
import type { PlaybackRegions } from 'js/src/luminary';
import {
    getVoiceDrivenAssetPlaybackRegions,
    getVoiceDrivenBaseAssetSourceDuration,
    translateVideoTimeToTranscriptTime,
} from 'js/src/luminary';
import {
    greaterThanOrEqualWithinEpsilon,
    lessThanOrEqualWithinEpsilon,
    lessThanWithinEpsilon,
} from 'js/src/luminary/comparison-utils';
import { getVoicedrivenSceneEndTime } from 'js/src/luminary/voicedriven-scenes';

export const TRANSCRIPT_EDITOR_CONFIG: CreateEditorArgs = {
    onError: function (error: Error) {
        ErrorReporter.capture(error);
    },
    namespace: 'TranscriptEditor',
    // in lexical, the editor is editable. But in the DOM
    // we mark the root element with `contentEditable=false`
    // so the user can't actually edit the transcript willy-nilly
    editable: true,
    nodes: [
        TranscriptWordNode,
        OverlayElementNode,
        DragHandleElementNode,
        BaseOverlayElementNode,
        TextCorrectElementNode,
        TrimElementNode,
        SilenceNode,
        SceneNumberNode,
        TrimSymbolNode,
    ],
};

const editorNamePrefix = 'ed';
const translationEditorNamePrefix = 'translation';
let nextEditorNum = 0;
export const getNextEditorName = () => `${editorNamePrefix}${nextEditorNum++}`;
export const getNextTranslationEditorName = () =>
    `${translationEditorNamePrefix}${nextEditorNum++}`;
export function createLexicalEditor(config: CreateEditorArgs, name?: string) {
    const newEditor = createEditor(config);
    if (!window._lexical_data) {
        window._lexical_data = {};
    }
    name = name || getNextEditorName();
    window._lexical_data[name] = (callback: (l: LexicalNode[]) => void) => {
        newEditor.update(() => {
            callback($dfs().map(({ node }) => node));
        });
    };
    return newEditor;
}

// Cap the number of overlapping word pairs attached to the error report to keep
// the Sentry payload bounded. We don't yet know how many overlaps a typical event
// has; overlappingWordCount below always carries the true total regardless.
const MAX_REPORTED_OVERLAPPING_WORDS = 25;

const buildOverlappingWordsContext = (overlappingWords: OverlappingWordPair[]): Context => {
    const maxOverlapAmount = overlappingWords.reduce(
        (max, overlap) => Math.max(max, overlap.overlapAmount),
        0,
    );
    return {
        extra: {
            overlappingWordCount: overlappingWords.length,
            maxOverlapAmount,
            overlappingWords: overlappingWords.slice(0, MAX_REPORTED_OVERLAPPING_WORDS),
        },
    };
};

let hasTriggeredOverlappingWordsError = false;
export async function updateEditor(
    editor: LexicalEditor,
    updateFn: () => void,
    options?: EditorUpdateOptions,
) {
    const stackTrace = new Error('Overlapping transcript words detected after update');
    const $reportOverlappingWords = () => {
        const overlappingWords = $getOverlappingWords();
        if (overlappingWords.length === 0 || hasTriggeredOverlappingWordsError) {
            return;
        }
        ErrorReporter.capture(stackTrace, buildOverlappingWordsContext(overlappingWords));
        hasTriggeredOverlappingWordsError = true;
    };

    return new Promise<void>((resolve) => {
        let hasResolved = false;
        const userOnUpdate = options?.onUpdate;

        editor.update(updateFn, {
            ...options,
            onUpdate: () => {
                if (hasResolved) return;
                hasResolved = true;
                editor.getEditorState().read($reportOverlappingWords);
                userOnUpdate?.();
                resolve();
            },
        });

        // If onUpdate isn't called (no state changes), resolve after current execution
        queueMicrotask(() => {
            if (hasResolved) return;
            hasResolved = true;
            editor.getEditorState().read($reportOverlappingWords);
            userOnUpdate?.();
            resolve();
        });
    });
}

export function $isNonDeletableNodeWhileTextEditing(node: LexicalNode): boolean {
    return $isSilenceNode(node) || $isSceneNumberNode(node);
}

export const $isSpace = (node: LexicalNode | null | undefined): node is TranscriptWordNode => {
    return $isTranscriptWordNode(node) && node.isEmptyText();
};

export const $isWhitespace = (node: LexicalNode | null | undefined) => {
    if ($isLineBreakNode(node) || $isSpace(node) || $isTrimElementNode(node)) {
        return true;
    }
    return false;
};

// we provide different type definitions for $findMatchWithin:
// - if the predicate is a type guard
// - if the predicate is a normal function
export function $findMatchWithin<T extends LexicalNode>(
    node: ElementNode,
    predicate: (node: LexicalNode) => node is T,
): T | null;
export function $findMatchWithin(
    node: ElementNode,
    predicate: (node: LexicalNode) => boolean,
): LexicalNode | null;
export function $findMatchWithin(node: ElementNode, predicate: (node: LexicalNode) => boolean) {
    for (const next of $dfsIteratorWithinNode(node)) {
        if (predicate(next.node)) {
            return next.node;
        }
    }
    return null;
}

// we provide different type definitions for $findLastMatchWithin:
// - if the predicate is a type guard
// - if the predicate is a normal function
export function $findLastMatchWithin<T extends LexicalNode>(
    node: ElementNode,
    predicate: (node: LexicalNode) => node is T,
): T | null;
export function $findLastMatchWithin(
    node: ElementNode,
    predicate: (node: LexicalNode) => boolean,
): LexicalNode | null;
export function $findLastMatchWithin(node: ElementNode, predicate: (node: LexicalNode) => boolean) {
    for (const next of $mirroredDfsIteratorWithinNode(node)) {
        if (predicate(next.node)) {
            return next.node;
        }
    }
    return null;
}

// we provide different type definitions for $findMatchInParents:
// - if the predicate is a type guard
// - if the predicate is a normal function
export function $findMatchInParents<T extends LexicalNode>(
    node: LexicalNode,
    predicate: (node: LexicalNode) => node is T,
): T | null;
export function $findMatchInParents(
    node: LexicalNode,
    predicate: (node: LexicalNode) => boolean,
): LexicalNode | null;
export function $findMatchInParents(node: LexicalNode, predicate: (node: LexicalNode) => boolean) {
    let currentNode: LexicalNode | null = node.getParent(); // do not include the node itself
    while (currentNode !== null) {
        if (predicate(currentNode)) {
            return currentNode;
        }
        currentNode = currentNode.getParent();
    }
    return null;
}

export const $getFirstNonEmptyWordWithin = (node: ElementNode): TranscriptWordNode | null => {
    return $findMatchWithin(node, (next): next is TranscriptWordNode => {
        return $isTranscriptWordNode(next) && !next.isEmptyText();
    });
};

export const $getLastNonEmptyWordWithin = (node: ElementNode): TranscriptWordNode | null => {
    return $findLastMatchWithin(node, (next): next is TranscriptWordNode => {
        return $isTranscriptWordNode(next) && !next.isEmptyText();
    });
};

export const $dfsIteratorForwardFrom = (startNode: LexicalNode): DFSIterator => {
    const lastNode = $getRoot().getLastDescendant();
    if (!lastNode) {
        return emptyDFSIterator;
    }
    const iterator = $dfsIterator(startNode, lastNode);
    iterator.next(); // skip the first node because that _is_ the startNode
    return iterator;
};

/**
 * @deprecated Use {@link $dfsIteratorForwardFrom} instead.
 */
export const $dfsForwardFrom = (startNode: LexicalNode): LexicalNode[] => {
    const nodeList: LexicalNode[] = [];
    const lastNode = $getRoot().getLastDescendant();
    if (!lastNode) {
        return nodeList;
    }
    $dfs(startNode, lastNode).forEach(({ node }) => {
        if (node !== startNode) {
            nodeList.push(node);
        }
    });
    return nodeList;
};

export const $dfsIteratorBackwardFrom = (startNode: LexicalNode): DFSIterator => {
    const iterator = $mirroredDfsIterator(startNode, $getRoot());
    iterator.next(); // skip the first node because that _is_ the startNode
    return iterator;
};

/**
 * @deprecated Use {@link $dfsIteratorBackwardFrom} instead.
 */
export const $dfsBackwardFrom = (startNode: LexicalNode) => {
    const nodeList: LexicalNode[] = [];
    $dfs($getRoot(), startNode) // do a depth-first search, ending at "node"
        .reverse() // then reverse the order of the nodes, so that we end up with a dfs going backwards, starting at "node"
        .forEach(({ node }) => {
            if (node !== startNode) {
                nodeList.push(node);
            }
        });
    return nodeList;
};

/**
 * @deprecated user {@link $dfsIteratorBetween} instead
 */
export const $dfsBetween = (startNode: LexicalNode, endNode: LexicalNode) => {
    const nodeList: LexicalNode[] = [];
    const isReversed = endNode.isBefore(startNode);
    const firstNode = isReversed ? endNode : startNode;
    const lastNode = isReversed ? startNode : endNode;
    $dfs(firstNode, lastNode).forEach(({ node }) => {
        if (node !== firstNode && node !== lastNode) {
            nodeList.push(node);
        }
    });
    return isReversed ? nodeList.reverse() : nodeList;
};

export const $dfsIteratorBetween = (startNode: LexicalNode, endNode: LexicalNode) => {
    const isReversed =
        endNode.isParentOf(startNode) ||
        (!startNode.isParentOf(endNode) && endNode.isBefore(startNode));
    const iterator = isReversed
        ? $mirroredDfsIterator(startNode, endNode)
        : $dfsIterator(startNode, endNode);
    iterator.next(); // skip the first node because that _is_ the firstNode
    return iterator;
};

/**
 * @deprecated Use {@link $dfsIteratorWithinNode} instead.
 */
export const $dfsWithinNode = (parentNode: ElementNode): LexicalNode[] => {
    const nodeList: LexicalNode[] = [];
    const lastDescendant = parentNode.getLastDescendant();
    if (!lastDescendant) {
        // there are no descendants. Just return an empty array
        return nodeList;
    }
    $dfs(parentNode, lastDescendant).forEach(({ node }) => {
        if (node !== parentNode) {
            nodeList.push(node);
        }
    });
    return nodeList;
};

export const $dfsIteratorWithinNode = (parentNode: ElementNode) => {
    const lastDescendant = parentNode.getLastDescendant();
    if (!lastDescendant) {
        // there are no descendants. Just return an empty iterator
        return emptyDFSIterator;
    }
    return $dfsIterator(parentNode, lastDescendant);
};

export const $mirroredDfsIteratorWithinNode = (parentNode: ElementNode) => {
    const lastDescendant = parentNode.getLastDescendant();
    if (!lastDescendant) {
        // there are no descendants. Just return an empty iterator
        return emptyDFSIterator;
    }
    return $mirroredDfsIterator(lastDescendant, parentNode);
};

/**
 * Returns the previous word in the transcript, traversing the tree as necessary
 */
export const $getPreviousTranscriptWordNode = (
    node: LexicalNode,
    {
        includeSilence = true,
        includeSpaces = false,
        includeEmptyWordFromTextCorrection = false,
        includeTrimmedWords = true,
    } = {},
): TranscriptWordNode | null => {
    for (const iteratorNodeVal of $dfsIteratorBackwardFrom(node)) {
        const iteratorNode = iteratorNodeVal.node;
        if ($isTranscriptWordNode(iteratorNode)) {
            if (!includeSilence && $isSilenceNode(iteratorNode)) {
                continue;
            }
            if (
                !includeSpaces &&
                iteratorNode.className !== EMPTY_WORD_CREATED_BY_TEXT_CORRECTION &&
                $isSpace(iteratorNode)
            ) {
                continue;
            }
            if (
                !includeEmptyWordFromTextCorrection &&
                iteratorNode.className === EMPTY_WORD_CREATED_BY_TEXT_CORRECTION
            ) {
                continue;
            }
            if (!includeTrimmedWords && $isTrimmedOut(iteratorNode)) {
                continue;
            }
            return iteratorNode;
        }
    }
    return null;
};

const $getTranscriptWordsBefore = (
    { fullTranscriptIncludingSpaces, indexByWordId }: TranscriptWordIndex,
    fromWord: TranscriptWordNode,
    numWords: number,
) => {
    let $startWord = fromWord;
    for (let i = 0; i < numWords; i++) {
        const $previousWord = $getPreviousTranscriptWordNode($startWord, {
            includeSpaces: false,
            includeSilence: true,
            includeEmptyWordFromTextCorrection: false,
        });
        $startWord = $previousWord || $startWord;
    }

    const indexOfStartWord = indexByWordId.get($startWord.id) ?? -1;
    return fullTranscriptIncludingSpaces.slice(
        Math.max(0, indexOfStartWord),
        indexByWordId.get(fromWord.id) ?? -1,
    );
};

/**
 * Returns the next word in the transcript, traversing the tree as necessary
 */
export const $getNextTranscriptWordNode = (
    node: LexicalNode,
    {
        includeSilence = true,
        includeSpaces = false,
        includeEmptyWordFromTextCorrection = false,
        includeTrimmedWords = true,
    } = {},
): TranscriptWordNode | null => {
    for (const iteratorNodeVal of $dfsIteratorForwardFrom(node)) {
        const iteratorNode = iteratorNodeVal.node;
        if ($isTranscriptWordNode(iteratorNode)) {
            if (!includeSilence && $isSilenceNode(iteratorNode)) {
                continue;
            }
            if (
                !includeSpaces &&
                iteratorNode.className !== EMPTY_WORD_CREATED_BY_TEXT_CORRECTION &&
                $isSpace(iteratorNode)
            ) {
                continue;
            }
            if (
                !includeEmptyWordFromTextCorrection &&
                iteratorNode.className === EMPTY_WORD_CREATED_BY_TEXT_CORRECTION
            ) {
                continue;
            }
            if (!includeTrimmedWords && $isTrimmedOut(iteratorNode)) {
                continue;
            }
            return iteratorNode;
        }
    }
    return null;
};

const $getTranscriptWordsAfter = (
    { fullTranscriptIncludingSpaces, indexByWordId }: TranscriptWordIndex,
    fromWord: TranscriptWordNode,
    numWords: number,
) => {
    // in space separated languages we should include the space next to selectedWord
    const $nextWord = $getNextTranscriptWordNode(fromWord, {
        includeSpaces: true,
        includeSilence: true,
    });
    // if we're at the end of the transcript, $nextWord will be null, or numWords can be 0 when we're in the beginning of the lookup of findTargetWordFromTranscript()
    if (!$nextWord || numWords === 0) {
        return [];
    }

    // we're looking up next words via $getNextTransriptWord, so the iteration should start from fromWord
    let $endWord = fromWord;
    for (let i = 0; i < numWords; i++) {
        const $takeNextWord = $getNextTranscriptWordNode($endWord, {
            includeSpaces: false,
            includeSilence: true,
        });
        $endWord = $takeNextWord || $endWord;
    }
    const indexOfEndWord = indexByWordId.get($endWord.id) ?? -1;
    // assume user seleted "c" in this string in space-separated language:
    // a b [c] d e
    // we should return [" ", "d"] if we're taking 1 word after "c"
    // if the language is not space-separated, the indices will be the same, so we'll just be returning ["d"]
    return fullTranscriptIncludingSpaces.slice(
        indexByWordId.get($nextWord.id) ?? -1,
        Math.min(fullTranscriptIncludingSpaces.length - 1, indexOfEndWord + 1),
    );
};

export const PRONUNCIATION_PREVIEW_CONTEXT_WORD_COUNT = 5;

/**
 * Collects the transcript words surrounding a selection so a pronunciation preview can be
 * spoken in the context of the sentence it appears in. Trimmed-out words and silence are
 * excluded because they are not spoken. Space nodes are collected as-is so the context is
 * spaced exactly like the transcript — languages that don't separate words with spaces
 * (e.g. Japanese) have no space nodes and get none injected.
 *
 * Context words carrying a word-level pronunciation override are spoken with that
 * override, mirroring how the video's audio is generated.
 */
export const $getPronunciationPreviewContext = (
    selectedWords: TranscriptWordNode[],
    numContextWords: number = PRONUNCIATION_PREVIEW_CONTEXT_WORD_COUNT,
): { contextBefore: string; contextAfter: string } => {
    const firstWord = selectedWords.at(0);
    const lastWord = selectedWords.at(-1);
    if (!firstWord || !lastWord) {
        return { contextBefore: '', contextAfter: '' };
    }

    const collectContextText = (fromWord: TranscriptWordNode, direction: 'before' | 'after') => {
        const traversalOptions = {
            includeSpaces: true,
            includeSilence: false,
            includeTrimmedWords: false,
        };
        const nodeTexts: string[] = [];
        const spokenPronunciationGroupIds = new Set<string>();
        let wordCount = 0;
        let lastCollectedWasSpace = false;
        let $word: TranscriptWordNode | null = fromWord;
        while (wordCount < numContextWords) {
            $word =
                direction === 'before'
                    ? $getPreviousTranscriptWordNode($word, traversalOptions)
                    : $getNextTranscriptWordNode($word, traversalOptions);
            if (!$word) {
                break;
            }
            const text = $word.getTextContent();
            if ($isSpace($word)) {
                // skipped silence/trimmed words can leave space nodes on both of their
                // sides; collapse those into a single space
                if (!lastCollectedWasSpace) {
                    nodeTexts.push(text);
                    lastCollectedWasSpace = true;
                }
                continue;
            }

            const { pronunciation, pronunciationId } = $word;
            if (pronunciation && pronunciationId) {
                // a multi-word override is spoken once for the whole group
                if (spokenPronunciationGroupIds.has(pronunciationId)) {
                    continue;
                }
                spokenPronunciationGroupIds.add(pronunciationId);
            }
            nodeTexts.push(pronunciation || text);
            lastCollectedWasSpace = false;
            wordCount++;
        }
        if (direction === 'before') {
            nodeTexts.reverse();
        }
        return nodeTexts.join('');
    };

    return {
        contextBefore: collectContextText(firstWord, 'before'),
        contextAfter: collectContextText(lastWord, 'after'),
    };
};

/**
 * Checks the lexical tree to make sure there is a space between the word that is passed in
 * and the next word
 * @param word - the word that we might add a space after
 */
export const $ensureSpaceAfterWord = (word: TranscriptWordNode) => {
    const nextWord = $getNextTranscriptWordNode(word, { includeSpaces: true });
    if (!$isSpace(nextWord)) {
        // there's a word, but it's not a space. Create a new space after the `word`
        // that was passed in.
        const spaceNode = $createTranscriptWordNode({
            text: ' ',
            startTime: word.endTime,
            endTime: word.endTime,
            isFiller: false, // spaces are not fillers
        });
        word.insertAfter(spaceNode);
    }
};

/**
 * Returns the first word in an overlay
 * If skipWhitespace = true, it will return the first non-whitespace node
 */
export const $getFirstWordInOverlay = (
    overlayElementNode: OverlayElementNode,
    { skipWhitespace = false } = {},
): TranscriptWordNode => {
    const result = $dfsForwardFrom(overlayElementNode).find((node) => {
        if ($isTranscriptWordNode(node)) {
            if (skipWhitespace) {
                return !$isWhitespace(node);
            } else {
                return true;
            }
        }
        return false;
    });
    if (!$isTranscriptWordNode(result)) {
        throw new Error('cannot find word in overlay');
    }
    return result;
};

/**
 * Returns the last word in an overlay
 * If skipWhitespace = true, it will return the last non-whitespace node
 */
export const $getLastWordInOverlay = (
    overlayElementNode: OverlayElementNode,
    { skipWhitespace = false } = {},
): TranscriptWordNode => {
    const lastDescendant = overlayElementNode.getLastDescendant();
    if (!lastDescendant) {
        throw new Error('cannot find word in overlay: no descendants');
    }
    const result = $dfsBackwardFrom(lastDescendant).find((node) => {
        if ($isTranscriptWordNode(node)) {
            if (skipWhitespace) {
                return !$isWhitespace(node);
            } else {
                return true;
            }
        }
        return false;
    });
    if (!$isTranscriptWordNode(result)) {
        throw new Error('cannot find word in overlay');
    }
    return result;
};

/**
 * takes a word and returns a tuple of first and last word in a scene
 */
export const $getFirstAndLastWordInSceneFromWord = (targetWordNode: TranscriptWordNode) => {
    const parentOverlayNode = $findMatchInParents(targetWordNode, $isOverlayElementNode);
    if (parentOverlayNode) {
        return [
            $getFirstWordInOverlay(parentOverlayNode, { skipWhitespace: true }),
            $getLastWordInOverlay(parentOverlayNode, { skipWhitespace: true }),
        ];
    } else {
        // base overlay, we'll want to take words to the left until we hit an overlay
        let leftMostNode: TranscriptWordNode = targetWordNode;
        // find the furthest-left TranscriptWordNode that is not empty
        let currentLeftNode = leftMostNode.getPreviousSibling();
        while (
            currentLeftNode &&
            ($isTranscriptWordNode(currentLeftNode) || $isLineBreakNode(currentLeftNode))
        ) {
            if ($isTranscriptWordNode(currentLeftNode) && !$isSpace(currentLeftNode)) {
                leftMostNode = currentLeftNode;
            }
            currentLeftNode = currentLeftNode.getPreviousSibling();
        }

        // same on the right side
        let rightMostNode: TranscriptWordNode = targetWordNode;
        // find the furthest-right TranscriptWordNode that is not empty
        let currentRightNode = rightMostNode.getNextSibling();
        while (
            currentRightNode &&
            ($isTranscriptWordNode(currentRightNode) || $isLineBreakNode(currentRightNode))
        ) {
            if ($isTranscriptWordNode(currentRightNode) && !$isSpace(currentRightNode)) {
                rightMostNode = currentRightNode;
            }
            currentRightNode = currentRightNode.getNextSibling();
        }
        return [leftMostNode, rightMostNode];
    }
};

/**
 * The words a scene starts and ends on, by scene id. Scenes that contain no words are absent.
 */
export type FirstAndLastWordIdsBySceneId = Record<
    string,
    { firstWordId: string; lastWordId: string }
>;

/**
 * Takes a list of scenes and returns a map of the first and last word in each scene
 * Used to realign scene times after editAIVoiceoverOperation
 *
 */
export const $getFirstLastWordIdsBySceneId = (
    scenes: readonly Scene[],
): FirstAndLastWordIdsBySceneId => {
    const sceneGroup = scenes[0].sceneGroup!;
    const voiceDrivenAssetPlaybackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
    const voiceDrivenAssetSourceDuration = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);

    let currentSceneIdx = 0;
    let currentScene = scenes[0];
    const result: FirstAndLastWordIdsBySceneId = {};
    $dfs().forEach(({ node }) => {
        if (!$isTranscriptWordNode(node) || $isTrimmedOut(node) || node.isEmptyText()) {
            return;
        }
        let sceneEndTime = translateVideoTimeToTranscriptTime(
            getVoicedrivenSceneEndTime(currentScene),
            voiceDrivenAssetPlaybackRegions,
            voiceDrivenAssetSourceDuration,
            sceneGroup.startTime,
            { includeTrailingCut: false },
        );
        while (lessThanWithinEpsilon(sceneEndTime, node.endTime)) {
            // The word is after the current scene, move to the next scene
            currentSceneIdx += 1;
            currentScene = scenes[currentSceneIdx];
            sceneEndTime = translateVideoTimeToTranscriptTime(
                getVoicedrivenSceneEndTime(currentScene),
                voiceDrivenAssetPlaybackRegions,
                voiceDrivenAssetSourceDuration,
                sceneGroup.startTime,
                { includeTrailingCut: false },
            );
        }
        if (!currentScene || result[currentScene.id]) {
            // We already have the first and last word for this scene, skip
            return;
        }
        const [firstWordNode, lastWordNode] = $getFirstAndLastWordInSceneFromWord(node);
        if (!firstWordNode || !lastWordNode) {
            throw new Error('could not find first or last word node in scene');
        }

        result[currentScene.id] = { firstWordId: firstWordNode.id, lastWordId: lastWordNode.id };
    });

    return result;
};

export const $isTrimmedOut = (word: LexicalNode) => {
    const trimAncestor = $getNearestNodeOfType(word, TrimElementNode);
    return !!trimAncestor; // if trimAncestor exists, the word is trimmed out
};

export function $getUntrimmedFillerWords(): TranscriptWordNode[] {
    return $nodesOfType(TranscriptWordNode).filter((node) => node.filler && !$isTrimmedOut(node));
}

export function $getSilenceSpans(): [SilenceNode, SilenceNode][] {
    const TRAVERSAL_SETTINGS = {
        includeSilence: true,
        includeSpaces: false,
        includeTrimmedWords: false,
    };
    const silenceSpans: [SilenceNode, SilenceNode][] = [];
    const visited = new Set<string>();

    for (const silenceNode of $nodesOfType(SilenceNode)) {
        if ($isTrimmedOut(silenceNode) || visited.has(silenceNode.getKey())) {
            continue;
        }
        visited.add(silenceNode.getKey());

        let beginningOfSilence = silenceNode;
        let endOfSilence = silenceNode;

        let leftTestingNode = $getPreviousTranscriptWordNode(silenceNode, TRAVERSAL_SETTINGS);
        while ($isSilenceNode(leftTestingNode)) {
            visited.add(leftTestingNode.getKey());
            beginningOfSilence = leftTestingNode;
            leftTestingNode = $getPreviousTranscriptWordNode(leftTestingNode, TRAVERSAL_SETTINGS);
        }

        let rightTestingNode = $getNextTranscriptWordNode(silenceNode, TRAVERSAL_SETTINGS);
        while ($isSilenceNode(rightTestingNode)) {
            visited.add(rightTestingNode.getKey());
            endOfSilence = rightTestingNode;
            rightTestingNode = $getNextTranscriptWordNode(rightTestingNode, TRAVERSAL_SETTINGS);
        }

        silenceSpans.push([beginningOfSilence, endOfSilence]);
    }

    return silenceSpans;
}

export const $isInOverlay = (node: LexicalNode) => {
    const overlayNode = $getOverlayParent(node);
    return !!overlayNode;
};

export const $getOverlayParent = (node: LexicalNode) => {
    const firstParent = node.getParent();
    if (!firstParent) {
        return null;
    }
    // $getNearestNodeOfType returns the node itself if it is the correct type.
    // so we pass in firstParent, so that we will only return true if `node` is IN
    // an BaseOverlayElementNode, not if it IS a BaseOverlayElementNode
    return $getNearestNodeOfType(firstParent, BaseOverlayElementNode);
};

export const $getTextCorrectElementNodeChildrenForGlobalEditing = () => {
    const root = $getRoot();
    return root.getChildren();
};

export const $isInTextCorrectNode = (node: LexicalNode) => {
    const firstParent = node.getParent();
    if (!firstParent) {
        return false;
    }
    // $getNearestNodeOfType returns the node itself if it is the correct type.
    // so we pass in firstParent, so that we will only return true if `node` is IN
    // a TextCorrectElementNode, not if it IS a TextCorrectElementNode
    const textCorrectNode = $getNearestNodeOfType(firstParent, TextCorrectElementNode);
    return !!textCorrectNode;
};

export const $getWordNodesBetweenTimes = (
    startTime: number,
    endTime: number,
    emptyWords = false,
): TranscriptWordNode[] => {
    const wordsBetweenTimes: TranscriptWordNode[] = [];
    for (const iteratorNode of $dfsIteratorForwardFrom($getRoot())) {
        const node = iteratorNode.node;
        if ($isTranscriptWordNode(node)) {
            if (node.startTime >= startTime && node.endTime <= endTime) {
                const isEmpty = node.isEmptyText();
                if (!emptyWords && isEmpty) {
                    continue;
                }
                wordsBetweenTimes.push(node);
            }
            if (node.endTime > endTime) {
                // since the nodes are in order, we can stop iterating
                break;
            }
        }
    }

    return wordsBetweenTimes;
};

/**
 * Returns start/end time for the provided word nodes with consideration of gaps between words
 * @param startNode LexicalNode - first word in the selection
 * @param endNode LexicalNode - last word in the selection
 * @param defaultStartTimeMillis number - start time boundary of the transcript - 0 or voiceDrivenAsset.startTime (if trimmed)
 * @param defaultEndTimeMillis number - end time boundary of the transcript - end of scene group or end of voiceDrivenAsset.endTime (if trimmed)
 * @returns { startTime, endTime } - start/end time of the given lexical nodes
 */
export const $getNodeTimeBounds = (
    startNode: TranscriptWordNode,
    endNode: TranscriptWordNode,
    playbackRegions: PlaybackRegions,
): { startTime: number; endTime: number } => {
    // get previous and next word nodes (non-whitespace)
    const prevNode = $getPreviousTranscriptWordNode(startNode);
    const nextNode = $getNextTranscriptWordNode(endNode);

    let startTime: number;
    let endTime: number;

    if (prevNode === null || $isTrimmedOut(prevNode)) {
        // we are at the start of the transcript, or just after a cut
        // find the cut end time between the startNode and prevNode
        const firstPlaybackRegionStart = playbackRegions[0][0];
        const prevNodeEndTime = prevNode ? prevNode.endTime : firstPlaybackRegionStart;
        startTime =
            playbackRegions
                .find(([start, end]) => {
                    return (
                        lessThanOrEqualWithinEpsilon(prevNodeEndTime, start) &&
                        lessThanOrEqualWithinEpsilon(start, startNode.startTime)
                    );
                })
                ?.at(0) || firstPlaybackRegionStart;
    } else {
        // Set the start of the overlay to be the midpoint of the previous word
        // and the first word in the overlay.
        startTime = wordGapMidpoint(prevNode, startNode);
    }

    if (nextNode === null || $isTrimmedOut(nextNode)) {
        // we are at the end of the transcript, or just before a cut
        // find the cut start time between the endNode and nextNode
        const lastPlaybackRegionEnd = playbackRegions[playbackRegions.length - 1][1];
        const nextNodeStartTime = nextNode ? nextNode.startTime : lastPlaybackRegionEnd;
        endTime =
            playbackRegions
                .find(([start, end]) => {
                    return (
                        lessThanOrEqualWithinEpsilon(endNode.endTime, end) &&
                        lessThanOrEqualWithinEpsilon(end, nextNodeStartTime)
                    );
                })
                ?.at(1) || lastPlaybackRegionEnd;
    } else {
        // Set the end of the overlay to be the midpoint of the last
        // word in the overlay and the next word.
        endTime = wordGapMidpoint(endNode, nextNode);
    }
    return { startTime, endTime };
};

export const $getOverlayElementNodeImmediatelyBefore = (
    node: LexicalNode,
): OverlayElementNode | null => {
    const prevNode = $getPreviousTranscriptWordNode(node);
    if (!prevNode) {
        return null;
    }
    return $getNearestNodeOfType(prevNode, OverlayElementNode);
};

export const $getOverlayElementNodeImmediatelyAfter = (
    node: LexicalNode,
): OverlayElementNode | null => {
    // if the arg passed in is an overlay, we want to get the next overlay.
    // Get the last word in this overlay, and then we can test the next
    // word to see if it's in an overlay.
    if ($isOverlayElementNode(node)) {
        node = $getLastWordInOverlay(node);
    }

    const nextNode = $getNextTranscriptWordNode(node);
    if (!nextNode) {
        return null;
    }
    return $getNearestNodeOfType(nextNode, OverlayElementNode);
};

export const $getOverlaySceneIdImmediatelyBefore = (node: LexicalNode): string | null => {
    const previousOverlay = $getOverlayElementNodeImmediatelyBefore(node);
    return previousOverlay ? previousOverlay.sceneId : null;
};

export const $getOverlaySceneIdImmediatelyAfter = (node: LexicalNode): string | null => {
    const nextOverlay = $getOverlayElementNodeImmediatelyAfter(node);
    return nextOverlay ? nextOverlay.sceneId : null;
};

export const $getWordNodes = (nodes: Array<LexicalNode>) => {
    return nodes.filter((node: LexicalNode): node is TranscriptWordNode => {
        if ($isTranscriptWordNodeAndNotSilence(node)) {
            return !node.isEmptyText();
        }
        return false;
    });
};

export const $nodesContainOnlyWords = (
    nodes: Array<LexicalNode>,
    { allowWords = true, allowLineBreaks = true, allowSilence = true, allowSpaces = true } = {},
): boolean => {
    if (!nodes) {
        return false;
    }
    return nodes.every((node) => {
        if (!$isTranscriptWordNode(node) && !$isLineBreakNode(node)) {
            // node is some other type of node, like maybe an DragHandleElementNode or something
            return false;
        }
        if (!allowLineBreaks && $isLineBreakNode(node)) {
            return false;
        }
        if (!allowSilence && $isSilenceNode(node)) {
            return false;
        }
        if (!allowSpaces && $isSpace(node)) {
            return false;
        }
        if (!allowWords && $isTranscriptWordNodeAndNotSilence(node) && !$isSpace(node)) {
            return false;
        }
        return true;
    });
};

export const $selectionContainsOnlyWords = (
    selection: BaseSelection,
    { allowWords = true, allowLineBreaks = true, allowSilence = true, allowSpaces = true } = {},
): boolean => {
    if (!selection) {
        return false;
    }
    const nodes = selection.getNodes();
    if (nodes.length === 0) {
        return false;
    }
    return $nodesContainOnlyWords(nodes, {
        allowSilence,
        allowSpaces,
        allowWords,
        allowLineBreaks,
    });
};

export const $getSelectionWordCount = (selection: BaseSelection | null): number => {
    if (!selection) {
        return 0;
    }
    let wordCount = 0;
    selection.getNodes().forEach((node) => {
        if ($isTranscriptWordNodeAndNotSilence(node) && !node.isEmptyText()) {
            wordCount++;
        }
    });
    return wordCount;
};

/**
 * @param node a word from the transcript
 * @param cutRegions output of {@link VideoData.getCutRegions}
 * @returns whether the word is trimmed out or not
 */
type TrimmedOutByCutRegionsReturnType =
    | { isCut: true; cutStart: number; cutEnd: number }
    | { isCut: false; cutStart: undefined; cutEnd: undefined };
export const $isNodeTrimmedOutByCutRegions = (
    node: TranscriptWordNode,
    cutRegions: Array<[number, number]>,
): TrimmedOutByCutRegionsReturnType => {
    const nodeStartTime = node.startTime;
    const nodeEndTime = node.endTime;
    for (let i = 0; i < cutRegions.length; i++) {
        const [cutStart, cutEnd] = cutRegions[i];
        if (
            theseAreOverlapping(nodeStartTime, nodeEndTime, cutStart, cutEnd, {
                includingTouchingEndpoints: false,
            })
        ) {
            return { cutStart, cutEnd, isCut: true };
        }
    }
    return { cutStart: undefined, cutEnd: undefined, isCut: false };
};

/**
 * @returns true if the two ranges there is any overlap between the two ranges passed in. Some example cases:
 *
 * Overlap - will return true
 * <---[--]--->
 * <-[------]->
 *
 * Some overlap, some not overlap - will return true
 * <--[-----]------>
 * <------[----]--->
 *
 * No overlap - will return false
 * <---[--]--->
 * <-[]------->
 *
 * Exact overlap - will return true
 * <---[--]--->
 * <---[--]--->
 *
 * Touching endpoints - will return true if includingTouchingEndpoints is true
 * <---[--]----->
 * <------[---]->
 */
export const theseAreOverlapping = (
    aStartTime: number,
    aEndTime: number,
    bStartTime: number,
    bEndTime: number,
    { includingTouchingEndpoints = true } = {},
) => {
    const lessThanComparator = includingTouchingEndpoints
        ? lessThanOrEqualWithinEpsilon
        : lessThanWithinEpsilon;
    return lessThanComparator(aStartTime, bEndTime) && lessThanComparator(bStartTime, aEndTime);
};

/**
 * @returns true if the time is within the range passed in. Some example cases:
 *
 * Within range - will return true
 * <---[---]--->
 * <-----|---->
 *
 * Not within range - will return false
 * <---[---]---->
 * <----------|->
 *
 * Touching the endpoint of the range - will return true if includingTouchingEndpoints is true
 * <---[---]---->
 * <-------|---->
 */
export const thisIsWithinRange = (
    time: number,
    [start, end]: [number, number],
    { includingTouchingEndpoints = true } = {},
) => {
    const lessThanComparator = includingTouchingEndpoints
        ? lessThanOrEqualWithinEpsilon
        : lessThanWithinEpsilon;
    return lessThanComparator(start, time) && lessThanComparator(time, end);
};

export const defragPlaybackRegions = (playbackRegions: [number, number][]): [number, number][] => {
    const returnValue: [number, number][] = [];
    for (let i = playbackRegions.length - 1; i >= 0; i--) {
        const currentRegion = playbackRegions[i];
        const nextRegion = playbackRegions[i + 1];
        if (
            typeof nextRegion !== 'undefined' &&
            greaterThanOrEqualWithinEpsilon(currentRegion[1], nextRegion[0])
        ) {
            // the current region overlaps with the next region, so we merge them
            returnValue.pop();
            returnValue.push([currentRegion[0], nextRegion[1]]);
        } else {
            returnValue.push([currentRegion[0], currentRegion[1]]);
        }
    }
    return returnValue;
};

/**
 * This function uses the transcript word timings to find a "fine" spot for an overlay boundary.
 * A "fine" spot is defined as a spot which is not in the middle of a word.
 *
 * This function has two "modes":
 *
 * - If getIdealOverlayBoundaryTime is false, then this function will return back the exact value of
 *   timeMillis, if it is a "fine" spot for an overlay boundary. This mode is useful to check if timeMillis
 *   is a valid time for an overlay boundary.
 * - If getIdealOverlayBoundaryTime is true, then this function will return back the ideal overlay
 *   boundary. The ideal overlay boundary time is the midpoint between neighbouring words. This mode is useful
 *   if you'd like to position an overlay boundary to the ideal spot in the transcript.
 * @param timeMillis - the input time. This function will find the closest acceptable time for an overlay boundary to the timeMillis
 * @param playbackRegions - the playback regions of the voice-driven asset
 * @param voiceDrivenAssetSourceDuration - the duration of the voice-driven asset's source
 * @param opts - options for the function
 * @param { boolean } opts.getIdealOverlayBoundaryTime - see the description of the function
 * @returns a time (millis) which is a valid spot for an overlay boundary
 */
export const $getFineOverlayBoundaryTimeCloseTo = (
    timeMillis: number,
    playbackRegions: PlaybackRegions,
    voiceDrivenAssetSourceDuration: number,
    { getIdealOverlayBoundaryTime } = { getIdealOverlayBoundaryTime: false },
): number => {
    const cutRegions: Array<[number, number]> = VideoData.getCutRegions(
        playbackRegions,
        voiceDrivenAssetSourceDuration,
    );
    const wordNodes = $dfs()
        .map(({ node }) => {
            if ($isTranscriptWordNode(node)) {
                return node;
            }
        })
        .filter(isNotUndefined)
        .filter((w) => !w.isEmptyText());

    for (let i = 0; i < wordNodes.length; i++) {
        const prevNode = i > 0 ? wordNodes[i - 1] : null;
        const node = wordNodes[i];
        const nextNode = i < wordNodes.length - 1 ? wordNodes[i + 1] : null;

        // TODO: probably we will need to make these all floating-point comparisons with epsilon???

        if (i === 0 && node.startTime >= timeMillis) {
            // timeMillis is before the first word.
            if (getIdealOverlayBoundaryTime) {
                // The ideal spot for the overlay boundary is the beginning of the sceneGroup, which
                // should equal the start of the voiceDriven asset
                return playbackRegions[0][0];
            }
            // Any time before the first word is a valid spot for an overlay
            return timeMillis;
        }

        if (i === wordNodes.length - 1 && node.endTime <= timeMillis) {
            // timeMillis is after the last word.
            if (getIdealOverlayBoundaryTime) {
                // The ideal sport for the overlay boundary is the end of the sceneGroup, which
                // should equal the end of the voiceDriven asset
                return playbackRegions[playbackRegions.length - 1][1];
            }
            // Any time after the last word is a valid spot for an overlay boundary
            return timeMillis;
        }

        if (node.endTime <= timeMillis && nextNode !== null && nextNode.startTime >= timeMillis) {
            // timeMillis is between two words.
            if (getIdealOverlayBoundaryTime) {
                const nodeCutData = $isNodeTrimmedOutByCutRegions(node, cutRegions);
                const nextNodeCutData = $isNodeTrimmedOutByCutRegions(nextNode, cutRegions);
                if (nodeCutData.isCut && !nextNodeCutData.isCut) {
                    // there is a cut that ends between this word and the next word.
                    // The ideal spot for an overlay is right on the cut end.
                    return nodeCutData.cutEnd;
                } else if (!nodeCutData.isCut && nextNodeCutData.isCut) {
                    // there is a cut that starts between this word and the next word.
                    // The ideal spot for an overlay is right on the cut start.
                    return nextNodeCutData.cutStart;
                } else if (nodeCutData.isCut && nextNodeCutData.isCut) {
                    // both nodes are cut.
                    // The ideal spot for an overlay is either before or after the cut.
                    // if the cut starts at 0, then choose after the cut. Otherwise, default to before.
                    return nodeCutData.cutStart > 0 ? nodeCutData.cutStart : nodeCutData.cutEnd;
                }
                // the ideal spot for an overlay boundary between two words is the midpoint
                // between the words
                return wordGapMidpoint(node, nextNode);
            }
            // Any time between words is a valid spot for an overlay boundary
            return timeMillis;
        }

        if (node.startTime < timeMillis && node.endTime > timeMillis) {
            // timeMillis is inside the word. Generally, that is NOT a fine spot for an overlay :(
            // The one exception is if the word is actually going to be cut out. Let's check for that:
            const nodeCutData = $isNodeTrimmedOutByCutRegions(node, cutRegions);
            if (nodeCutData.isCut) {
                // yes, this word will be cut out. So the timeMillis is still a fine spot for an overlay
                if (getIdealOverlayBoundaryTime) {
                    // the ideal spot for an overlay boundary is right before or after the cut
                    // if the cut starts at 0, then choose after the cut. Otherwise, default to before.
                    return nodeCutData.cutStart > 0 ? nodeCutData.cutStart : nodeCutData.cutEnd;
                }
                // any time that is inside a trimmed-out word is a valid spot for an overlay boundary
                return timeMillis;
            }

            // Now we know that timeMillis is NOT a fine spot for an overlay.
            // Find the closest time that IS fine and return that (now we will only ever return ideal times, since
            // we know that the timeMillis passed in is not a valid spot for an overlay boundary)
            const distanceToStart = Math.abs(node.startTime - timeMillis);
            const distanceToEnd = Math.abs(node.endTime - timeMillis);

            if (distanceToStart < distanceToEnd) {
                if (prevNode) {
                    // return the mid-point between this node and the previous node
                    return wordGapMidpoint(prevNode, node);
                } else {
                    // this is the first word, so we need to return the start of the voiceDriven asset
                    return playbackRegions[0][0];
                }
            } else {
                if (nextNode) {
                    // return the mid-point between this node and the next node
                    return wordGapMidpoint(node, nextNode);
                } else {
                    // this is the last word, so we need to return the end of the voiceDriven asset
                    return playbackRegions[playbackRegions.length - 1][1];
                }
            }
        }
    }

    // this should never happen
    throw new Error('Could not find a valid overlay boundary time');
};

/**
 * Given a voiceDrivenAssetStartTime, this function finds and returns the first word in the transcript
 * which is not trimmed out of the transcript. If the voiceDrivenAssetStartTime is 0, then this
 * will be the first TranscriptWordNode in the transcript.
 * @param voiceDrivenAssetStartTime - the trimmed start time of the voiceDrivenAsset
 * @returns TranscriptWordNode
 */
export const $getFirstTranscriptWordShowing = (
    voiceDrivenAssetStartTime: number,
): TranscriptWordNode => {
    for (const iteratorNodeVal of $dfsIteratorForwardFrom($getRoot())) {
        const node = iteratorNodeVal.node;
        if (!$isTranscriptWordNode(node)) {
            continue;
        }
        if (node.isEmptyText()) {
            continue;
        }
        if (node.startTime < voiceDrivenAssetStartTime) {
            continue;
        }
        return node;
    }
    throw new Error('could not find first word in transcript');
};

/**
 * Similar to $getFirstTranscriptWordShowing, but for the end of the transcript. Returns the
 * last TranscriptWordNode in the transcript which is not trimmed out by the voiceDrivenAssetEndTime
 * @param voiceDrivenAssetEndTime - the trimmed end time of the voiceDrivenAsset
 * @returns TranscriptWordNode
 */
export const $getLastTranscriptWordShowing = (
    voiceDrivenAssetEndTime: number,
): TranscriptWordNode => {
    const lastNode = $getRoot().getLastDescendant();
    if (!lastNode) {
        throw new Error('no nodes in transcript');
    }
    for (const iteratorNodeVal of $dfsIteratorBackwardFrom(lastNode)) {
        const node = iteratorNodeVal.node;
        if (!$isTranscriptWordNode(node)) {
            continue;
        }
        if (node.isEmptyText()) {
            continue;
        }
        if (node.endTime > voiceDrivenAssetEndTime) {
            continue;
        }
        return node;
    }
    throw new Error('could not find last word in transcript');
};

export const getWordAtTime = (
    editor: LexicalEditor,
    timeMillis: number,
): TranscriptWordNode | null => {
    return editor.getEditorState().read(() => {
        for (const iteratorNode of $dfsIteratorForwardFrom($getRoot())) {
            const node = iteratorNode.node;
            if ($isTranscriptWordNode(node)) {
                if (node.startTime > timeMillis || node.endTime < timeMillis) {
                    continue;
                }
                return node;
            }
        }
        return null;
    });
};

export const $getWordNodeByWordId = (wordId: string): TranscriptWordNode | undefined => {
    const result = $dfs().find(({ node }) => {
        if ($isTranscriptWordNode(node)) {
            return node.id === wordId;
        }
    })?.node;
    if ($isTranscriptWordNode(result)) {
        return result;
    }
};

/**
 * Similar to getWordAtTime but it will get the next word closest to the given time
 */
export const $getNextWordAtTime = (timeMillis: number): TranscriptWordNode | null => {
    for (const iteratorNode of $dfsIteratorForwardFrom($getRoot())) {
        const node = iteratorNode.node;
        if ($isTranscriptWordNode(node) && !$isWhitespace(node)) {
            if (node.startTime >= timeMillis) {
                return node;
            }
        }
    }
    return null;
};

export const $getPreviousWordAtTime = (timeMillis: number): TranscriptWordNode | null => {
    const lastNode = $getRoot().getLastDescendant();
    if (!lastNode) {
        return null;
    }
    for (const iteratorNode of $dfsIteratorBackwardFrom(lastNode)) {
        const node = iteratorNode.node;
        if ($isTranscriptWordNode(node) && !$isWhitespace(node)) {
            if (node.endTime <= timeMillis) {
                return node;
            }
        }
    }
    return null;
};

export type OverlappingWordPair = {
    previousWord: string;
    previousStartTime: number;
    previousEndTime: number;
    word: string;
    startTime: number;
    endTime: number;
    // how much `word` starts before `previousWord` ends (same unit as word times)
    overlapAmount: number;
};

export const $getOverlappingWords = (): OverlappingWordPair[] => {
    const overlappingWords: OverlappingWordPair[] = [];

    let previousWord: TranscriptWordNode | null = null;
    $dfs().forEach(({ node }) => {
        // Spaces are zero-duration placeholders that copy the preceding word's end
        // time when they are created, so re-splicing the words around one leaves a
        // stale start time behind. Nothing positions audio from a space, so skip
        // them here the same way every other word-walking helper does.
        if (!$isTranscriptWordNode(node) || $isWhitespace(node)) {
            return;
        }
        if (previousWord && node.startTime < previousWord.endTime) {
            overlappingWords.push({
                previousWord: previousWord.getTextContent(),
                previousStartTime: previousWord.startTime,
                previousEndTime: previousWord.endTime,
                word: node.getTextContent(),
                startTime: node.startTime,
                endTime: node.endTime,
                overlapAmount: previousWord.endTime - node.startTime,
            });
        }
        previousWord = node;
    });

    return overlappingWords;
};

export type GetTranscriptWordsOptions = {
    includeEmptyWords?: boolean;
    includeTrimmedWords?: boolean;
    includeSilence?: boolean;
};

export function $getTranscriptWordNodes(
    opts: GetTranscriptWordsOptions = {},
): TranscriptWordNode[] {
    const transcriptWordNodes: TranscriptWordNode[] = [];
    const nodes: LexicalNode[] = $dfs().map(({ node }) => node);
    for (const node of nodes) {
        if ($isTranscriptWordNode(node)) {
            if (!opts.includeEmptyWords && node.isEmptyText()) {
                continue;
            }
            if (!opts.includeTrimmedWords && $isTrimmedOut(node)) {
                continue;
            }
            if (!opts.includeSilence && $isSilenceNode(node)) {
                continue;
            }
            transcriptWordNodes.push(node);
        }
    }
    return transcriptWordNodes;
}

export function getTranscriptWords(
    editor: LexicalEditor,
    opts: GetTranscriptWordsOptions = {},
): TranscriptWord[] {
    const transcriptWords: TranscriptWord[] = [];
    editor.getEditorState().read(() => {
        const nodes: LexicalNode[] = $dfs().map(({ node }) => node);
        for (const node of nodes) {
            if ($isTranscriptWordNode(node)) {
                if (!opts.includeEmptyWords && node.isEmptyText()) {
                    continue;
                }
                if (!opts.includeTrimmedWords && $isTrimmedOut(node)) {
                    continue;
                }
                if (!opts.includeSilence && $isSilenceNode(node)) {
                    continue;
                }
                transcriptWords.push({
                    word: node.getTextContent(),
                    startTime: node.startTime,
                    endTime: node.endTime,
                    filler: node.filler,
                });
            }
        }
    });
    return transcriptWords;
}

/**
 * Returns the pauses (silence) in a transcript editor as merged time spans, in document
 * order. The editor stores a pause as a run of adjacent SilenceNodes (each ~1 second); this
 * merges each run into a single span so callers see one logical pause rather than N
 * one-second chunks. Times are in milliseconds, matching the word node timings.
 */
export function getTranscriptPauses(
    editor: LexicalEditor,
): { startTime: number; endTime: number }[] {
    const pauses: { startTime: number; endTime: number }[] = [];
    editor.getEditorState().read(() => {
        let current: { startTime: number; endTime: number } | null = null;
        $dfs().forEach(({ node }) => {
            if (!$isTranscriptWordNode(node) || $isTrimmedOut(node)) {
                return;
            }
            if ($isSilenceNode(node)) {
                if (current) {
                    current.endTime = node.endTime;
                } else {
                    current = { startTime: node.startTime, endTime: node.endTime };
                    pauses.push(current);
                }
            } else if (!node.isEmptyText()) {
                // Any non-silence word ends the current silence run. Silence nodes in a pause
                // are adjacent (no space node sits between them), so this never splits a run.
                current = null;
            }
        });
    });
    return pauses;
}

// Gets a list of silence nodes before the given node up until the first non-empty word node
export function $getSilenceNodesBefore(node: LexicalNode) {
    const result: SilenceNode[] = [];
    for (const iteratorNode of $dfsIteratorBackwardFrom(node)) {
        const node = iteratorNode.node;
        if ($isTrimmedOut(node)) {
            continue;
        }
        if ($isSpace(node) || $isWhitespace(node)) {
            continue;
        }
        // Exit when we reach a non-space word node
        if ($isTranscriptWordNodeAndNotSilence(node)) {
            break;
        }
        if ($isSilenceNode(node)) {
            result.push(node);
        }
    }
    return result.reverse(); // Return nodes in the order they appear in the transcript
}

// Gets a list of silence nodes after the given node up until the first non-empty word node
export function $getSilenceNodesAfter(node: LexicalNode) {
    const result: SilenceNode[] = [];
    for (const iteratorNode of $dfsIteratorForwardFrom(node)) {
        const node = iteratorNode.node;
        if ($isTrimmedOut(node)) {
            continue;
        }
        if ($isSpace(node) || $isWhitespace(node)) {
            continue;
        }
        // Exit when we reach a non-space word node
        if ($isTranscriptWordNodeAndNotSilence(node)) {
            break;
        }
        if ($isSilenceNode(node)) {
            result.push(node);
        }
    }
    return result;
}

export function $getSilenceNodesAroundNode(node: TranscriptWordNode) {
    let startNode: TranscriptWordNode = node;
    if ($isSilenceNode(node) || $isWhitespace(node)) {
        const previousNonSilenceNode = $getPreviousTranscriptWordNode(node, {
            includeSilence: false,
        });
        if (previousNonSilenceNode) {
            startNode = previousNonSilenceNode;
        }
    }
    return $getSilenceNodesAfter(startNode);
}

/**
 * This function takes in a start and end time of two words and determines how many silences nodes
 * should fill that gap time and returns the start and end times of each silence node.
 *
 * Example: When target duration is 1000ms
 * - 0-1001 millis = 0 silence nodes
 * - 1002-1499 millis = 1 silence node
 * - 1500-2499 millis = 2 silence nodes...
 */
export function getSilenceNodeTimes(startTimeMillis: number, endTimeMillis: number) {
    const pauseMillis = endTimeMillis - startTimeMillis;
    if (pauseMillis < TRANSCRIPT_CONFIG.SILENCE_NODE_THRESHOLD_MILLIS) {
        return [];
    }
    const numSilenceNodes = Math.round(
        pauseMillis / TRANSCRIPT_CONFIG.SILENCE_NODE_TARGET_DURATION_MILLIS,
    );
    if (!numSilenceNodes) {
        return [];
    }
    const actualSilenceDuration = pauseMillis / numSilenceNodes;
    let silenceStartTime = startTimeMillis;
    const silences: SilenceNodeData[] = [];
    for (let i = 0; i < numSilenceNodes; i++) {
        const silenceEndTime = Math.min(silenceStartTime + actualSilenceDuration, endTimeMillis);
        silences.push({ startTime: silenceStartTime, endTime: silenceEndTime });
        silenceStartTime = silenceEndTime;
    }
    return silences;
}

/**
 * In many of our AI VO operations we must get all LexicalNodes in EditedTranscriptWord format (ie. marking word nodes
 * as user selected). This utility function should be used to get that full transcript. By default it'll mark all words
 * in the transcript as user selected.
 *
 * @param editor - LexicalEditor instance
 * @param userSelectedWordIds - id[]: pass this array if you only want to mark specific words as user selected
 * @returns EditedTranscriptWordRequest[] - used for AI VO endpoints
 */
export const getFullTranscriptFromEditor = (
    editor: LexicalEditor,
    userSelectedWordIds: string[] = [],
    opts: { includeSilence?: boolean; includeSpace?: boolean } = {},
) => {
    const markAllWordsAsUserSelected = userSelectedWordIds.length === 0;
    const fullTranscript = editor.getEditorState().read(() => {
        const full_transcript: EditedTranscriptWordRequest[] = [];
        $dfs().forEach(({ node }) => {
            if (!$isTranscriptWordNode(node)) {
                return;
            }
            const isTrimmedOut = $isTrimmedOut(node);
            let userSelected = false;
            if (!isTrimmedOut) {
                if (markAllWordsAsUserSelected) {
                    userSelected = true;
                } else if (userSelectedWordIds.includes(node.id)) {
                    userSelected = true;
                }
            }
            if (!opts.includeSilence && $isSilenceNode(node)) {
                // Skip and continue to next node
                return;
            }
            if ($isSilenceNode(node)) {
                full_transcript.push({
                    id: node.id,
                    word: node.transcriptWordTextContent,
                    startTime: node.startTime / 1000,
                    endTime: node.endTime / 1000,
                    userSelected,
                });
            } else if ($isSpace(node) && opts.includeSpace) {
                full_transcript.push({
                    id: node.id,
                    word: node.getTextContent(),
                    startTime: node.startTime / 1000,
                    endTime: node.endTime / 1000,
                    userSelected,
                });
            } else if (
                !$isWhitespace(node) ||
                $isEmptyTranscriptWordNodeCreatedByTextCorrection(node)
            ) {
                full_transcript.push({
                    id: node.id,
                    word: node.getTextContent(),
                    startTime: node.startTime / 1000,
                    endTime: node.endTime / 1000,
                    userSelected,
                    pronunciation: node.pronunciation,
                    pronunciationId: node.pronunciationId,
                });
            }
        });
        return full_transcript;
    });

    // the userSelectedWordIds might not necessarily be in a continuous stretch. It might be like:
    // [userSelected, userSelected, NOTUserSelected, NOTUserSelected, userSelected, NOTUserSelected]
    // however, the backend can only handle a single continuous stretch of userSelected words. So we need to
    // mark all words between the first and last userSelected word as userSelected.
    // That would convert the above example into:
    // [userSelected, userSelected, userSelected, userSelected, userSelected, NOTUserSelected]
    const firstUserSelectedWordIndex = fullTranscript.findIndex((word) => word.userSelected);
    const lastUserSelectedWordIndex = fullTranscript.findLastIndex((word) => word.userSelected);
    if (firstUserSelectedWordIndex !== -1) {
        for (let i = firstUserSelectedWordIndex; i <= lastUserSelectedWordIndex; i++) {
            fullTranscript[i].userSelected = true;
        }
    }
    return fullTranscript;
};

/**
 * Many plugins need to get selected nodes from the Lexical Selection. This util function hopes to unify the selection
 * logic.
 *
 * @returns LexicalNode[] - selected nodes based on current selection of the editor
 */
export const $getSelectedNodes = () => {
    const currentSelection = $getSelection();
    if (!currentSelection) {
        return [];
    }
    return currentSelection.getNodes();
};

// backend calls 'text'.strip(PUNCTUATIONS), so we need to do the same here
function stripPunctuation(str: string) {
    const regex = new RegExp(
        `^${STRIPPED_PUNCTUATIONS_REGEX.source}+|${STRIPPED_PUNCTUATIONS_REGEX.source}+$`,
        'g',
    );
    return str.replace(regex, '');
}

export const convertWordNodesToText = (words: LexicalNode[]) => {
    const transcriptText = words
        .filter((word) => $isTranscriptWordNode(word))
        .map((word) => word.getTextContent())
        .join('');
    const strippedText = stripPunctuation(transcriptText).trim();
    return strippedText;
};

/**
 * Words carrying a word-level pronunciation override share a `pronunciationId` and are
 * spoken as a single phrase, so such an override can only be edited as a whole. Returns the
 * whole phrase a selection is part of — the span covering the selected words and every word
 * of every override group they touch, spaces included — or null when the selection carries
 * no override.
 */
export const $getOverridePhraseWords = (selectedWords: TranscriptWordNode[]) => {
    const groupIds = new Set<string>();
    for (const word of selectedWords) {
        if (word.pronunciationId) {
            groupIds.add(word.pronunciationId);
        }
    }
    if (groupIds.size === 0) {
        return null;
    }

    const selectedWordIds = new Set(selectedWords.map((word) => word.id));
    const isPartOfPhrase = (word: TranscriptWordNode) =>
        selectedWordIds.has(word.id) ||
        (word.pronunciationId !== undefined && groupIds.has(word.pronunciationId));

    // Include the empty words so the phrase keeps the spacing between its words
    const fullTranscriptIncludingSpaces = $getTranscriptWordNodes({
        includeEmptyWords: true,
        includeTrimmedWords: false,
        includeSilence: true,
    });
    const firstIndex = fullTranscriptIncludingSpaces.findIndex(isPartOfPhrase);
    const lastIndex = fullTranscriptIncludingSpaces.findLastIndex(isPartOfPhrase);
    if (firstIndex === -1) {
        return null;
    }
    return fullTranscriptIncludingSpaces.slice(firstIndex, lastIndex + 1);
};

type TranscriptWordIndex = {
    fullTranscriptIncludingSpaces: TranscriptWordNode[];
    indexByWordId: Map<string, number>;
};

export type PronunciationMatcher = {
    transcriptWordIndex: TranscriptWordIndex;
    pronunciationTargetWords: Set<string>;
};

export const $createPronunciationMatcher = (
    sortedPronunciations: readonly DeepReadonly<Pronunciation>[],
): PronunciationMatcher => {
    const fullTranscriptIncludingSpaces = $getTranscriptWordNodes({
        includeEmptyWords: true,
        includeTrimmedWords: false,
        includeSilence: true,
    });
    return {
        transcriptWordIndex: {
            fullTranscriptIncludingSpaces,
            indexByWordId: new Map(fullTranscriptIncludingSpaces.map((word, i) => [word.id, i])),
        },
        pronunciationTargetWords: new Set(
            sortedPronunciations.map((pronunciation) => pronunciation.target_word),
        ),
    };
};

export const $matchSelectedWordsToPronunciation = (
    { transcriptWordIndex, pronunciationTargetWords }: PronunciationMatcher,
    selectedWords: TranscriptWordNode[],
) => {
    const numWords = selectedWords.length;
    const firstNode = selectedWords[0];
    const lastNode = selectedWords[selectedWords.length - 1];

    /**
     * Find all possible combinations of target words that can be formed
     * from the selected nodes by adding words before and after the selected nodes.
     *
     * Consider the following sentence: a b c d e f g
     * Here, `d` is the selected node(s) and [...] will represents the window of target words.

        * The following loops will generate the following target word combinations:
        * - a b c [d] e f g
        * - a b c [d e] f g
        * - a b [c d] e f g
        * - a b c [d e f] g
        * - a b [c d e] f g
        * - a [b c d] e f g
        *
        * Currently max pronunciation words are 3, so we stop there and
        * reverse the target word combinations to prioritize longer target words.
        */
    const targetWordCombinations = new Set<string>();
    for (let windowSize = numWords; windowSize <= MAX_PRONUNCIATION_WORDS; windowSize++) {
        const totalWordsNeeded = windowSize - numWords;
        for (
            let wordsNeededBefore = 0, wordsNeededAfter = totalWordsNeeded;
            wordsNeededAfter >= 0;
            wordsNeededBefore++, wordsNeededAfter--
        ) {
            targetWordCombinations.add(
                convertWordNodesToText([
                    ...$getTranscriptWordsBefore(transcriptWordIndex, firstNode, wordsNeededBefore),
                    ...selectedWords,
                    ...$getTranscriptWordsAfter(transcriptWordIndex, lastNode, wordsNeededAfter),
                ]),
            );
        }
    }

    const checkTargetWords = Array.from(targetWordCombinations).reverse(); // reverse to prioritize longer target words
    for (const targetWord of checkTargetWords) {
        if (pronunciationTargetWords.has(targetWord)) {
            return { targetWord, isMatching: true };
        }
    }

    return { targetWord: convertWordNodesToText(selectedWords), isMatching: false };
};

export const $findTargetWordsFromSelectedWordsInTranscript = (
    sortedPronunciations: readonly DeepReadonly<Pronunciation>[],
    selectedWords: TranscriptWordNode[],
) =>
    $matchSelectedWordsToPronunciation(
        $createPronunciationMatcher(sortedPronunciations),
        selectedWords,
    );

/**
 * The LuminarySyncPlugin runs editor.update(), which internally is async (it uses queueMicrotask internally).
 * Sometimes, in our code we get an event (like a click or a selection change or something), and we want to
 * run some code on that event, but the LuminarySyncPlugin could completely re-write all of the DOM nodes.
 * As a result, this utility function is provided.
 *
 * The implementation is a little silly: it just uses setTimeout. We could make it more fancy by actually
 * connecting it to the LuminarySyncPlugin somehow. But this is good enough for now!
 */
export const waitForLuminarySyncPluginThen = (cb: () => void) => {
    window.setTimeout(() => {
        cb();
    }, 1);
};

/**
 * When calling `node.exportJSON()`, the children of the node are not serialized.
 * This function is called recursively to serialize the node passed in, plus all of its descendants.
 * @param node any LexicalNode
 * @returns the node, serialized to JSON. If there are any children, those will also be serialized
 */
export function exportNodeToJSON(node: LexicalNode): SerializedLexicalNode {
    const serializedNode = node.exportJSON();
    if ($isElementNode(node)) {
        const serializedChildren = (serializedNode as SerializedElementNode).children; // if node is an ELementNode, serializedNode is a SerializedElementNode
        if (!Array.isArray(serializedChildren)) {
            throw new Error('ElementNode children must be an array');
        }

        // mutate the serializedChildren array, adding the JSON representation of each child node
        const children = node.getChildren();
        for (let i = 0; i < children.length; i++) {
            const child = children[i];
            const serializedChildNode = exportNodeToJSON(child);
            serializedChildren.push(serializedChildNode);
        }
    }
    return serializedNode;
}

export function getTranscriptDuration(wordList: EditedTranscriptWord[]): number {
    if (wordList.length === 0) {
        return 0;
    }
    return Math.max(...wordList.map((word) => word.endTime));
}
